import { TextInput } from "@mantine/core";
import React, { useEffect, useRef } from "react";

import _ from "lodash";
import { useServerApi } from "../hooks/userServerApi";
import { useTranslation } from "react-i18next";
import FormReadOnlyField from "./formReadOnlyField";
import { isWidgetReadOnly } from "../helpers/formReadOnly";
import { omitFormRenderContext } from "../helpers/omitFormRenderContext";

const UniqueTextInput = ({
  form,
  name,
  uniqueEntity,
  errorMessage = "Not unqiue", //ID not Unqiue error message
  formatMessage = "Please enter a valid ID", //ID format error message
  validation,
  readOnly = false,
  readonly = false,
  disabled = false,
  label,
  ...props
}) => {
  const value = _.get(form.values, name);
  const id = _.get(form.values, "_id");
  const { t } = useTranslation();
  const requestIdRef = useRef(0);
  const locked = isWidgetReadOnly({ disabled, readOnly, readonly, form });

  const [api] = useServerApi();

  const setPending = (pending) => {
    form.setUniqueCheckPending?.(name, pending);
  };

  const checkFormat = () => {
    if (!validation) return true;
    if (!validation(value, form.values)) {
      form.setFieldError(name, t(formatMessage));
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (locked) {
      setPending(false);
      return;
    }

    let cancelled = false;

    const runChecks = async () => {
      if (!checkFormat()) {
        setPending(false);
        return;
      }

      if (_.isEmpty(value)) {
        setPending(false);
        return;
      }

      const requestId = ++requestIdRef.current;
      setPending(true);

      try {
        const result = await api.search({
          apiEntity: uniqueEntity,
          searchQuery: { [name]: value?.trim() },
        });

        if (cancelled || requestId !== requestIdRef.current) return;

        if (!_.isEmpty(result.docs) && id !== result.docs[0]._id) {
          form.setFieldError(name, t(errorMessage));
        } else {
          const currentError = form.errors?.[name];
          if (currentError === t(errorMessage)) {
            form.clearFieldError(name);
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
        if (!cancelled && requestId === requestIdRef.current) {
          setPending(false);
        }
      }
    };

    runChecks();

    return () => {
      cancelled = true;
      setPending(false);
    };
  }, [locked, value, id, uniqueEntity, name]);

  if (locked) {
    const display =
      value === null || value === undefined || value === "" ? "-" : String(value);
    return <FormReadOnlyField label={label} value={display} />;
  }

  return <TextInput label={label} {...omitFormRenderContext(props)} />;
};

export default UniqueTextInput;
